import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import type { U2000AutoCompleteSearchResult } from "../u2000Core/types/U2000AutoCompleteSearchResult";
import { u2000ExtractAutoCompleteResults } from "../u2000Core/utils/u2000ExtractAutoCompleteResults";
import {
  doFindAvailablePeriodsForDisplay,
  doFindEmployeeForDisplay,
  doFindProjectForDisplay,
  doFindSubprojectForDisplay,
  doFindTasksForProjectForDisplay,
  luHandleApiError,
} from "./U3130Service";
import type {
  U3130AutoCompletCriterias,
  U3130FindAvailablePeriodsRequest,
  U3130FindEmployeeItem,
  U3130FindProjectItem,
  U3130FindSubprojectItem,
  U3130FindTasksForProjectRequest,
  U3130TaskItem,
} from "./U3130TimesheetModel";
import { U3130_NS as NS } from "./U3130I18n";

export type U3130FindSearchCriterias = U3130AutoCompletCriterias;

function buildFindTasksForProjectCacheKey(body: U3130FindTasksForProjectRequest): string {
  const sub = body.subprojectId == null || String(body.subprojectId).trim() === "" ? "" : String(body.subprojectId).trim();
  return `${body.projectId.trim()}::${sub}`;
}

const findTasksForProjectResolvedCache = new Map<string, U3130TaskItem[]>();
const findTasksForProjectInflight = new Map<string, Promise<U3130TaskItem[]>>();

export function useU3130FindProjectHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3130FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<U3130FindProjectItem>> => {
      try {
        const response = await doFindProjectForDisplay(axios, criterias);
        return u2000ExtractAutoCompleteResults<U3130FindProjectItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast],
  );

  return { search };
}

export interface U3130FindSubprojectHandlersParams {
  getProjectId: () => string | undefined | null;
}

export function useU3130FindSubprojectHandlers(params: U3130FindSubprojectHandlersParams) {
  const { getProjectId } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3130FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<U3130FindSubprojectItem>> => {
      try {
        const projectId = getProjectId();
        const criteriasWithProject =
          projectId != null && projectId.trim() !== ""
            ? [...criterias, { fieldName: "projectId", value: projectId }]
            : criterias;
        const response = await doFindSubprojectForDisplay(axios, criteriasWithProject);
        return u2000ExtractAutoCompleteResults<U3130FindSubprojectItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast, getProjectId],
  );

  return { search };
}

export function useU3130FindEmployeeHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3130FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<U3130FindEmployeeItem>> => {
      try {
        const response = await doFindEmployeeForDisplay(axios, criterias);
        return u2000ExtractAutoCompleteResults<U3130FindEmployeeItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast],
  );

  return { search };
}

export function useU3130FindAvailablePeriodsHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const fetchAvailablePeriods = useCallback(
    async (body: U3130FindAvailablePeriodsRequest): Promise<string[]> => {
      try {
        const response = await doFindAvailablePeriodsForDisplay(axios, body);
        const data = response.data;
        if (data == null) return [];
        const results = Array.isArray(data) ? data : (data as { results?: unknown }).results;
        if (!Array.isArray(results)) return [];
        const out: string[] = [];
        for (const item of results) {
          if (typeof item === "string") {
            out.push(item.slice(0, 10));
          } else if (item && typeof item === "object") {
            const o = item as Record<string, unknown>;
            const v = o.periodDate ?? o.value ?? o.period;
            if (typeof v === "string") {
              out.push(v.slice(0, 10));
            }
          }
        }
        return out.filter((s) => s.length === 10);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return [];
      }
    },
    [axios, t, toast],
  );

  return { fetchAvailablePeriods };
}

export function useU3130FindTasksForProjectHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const fetchTasks = useCallback(
    async (body: U3130FindTasksForProjectRequest): Promise<U3130TaskItem[]> => {
      const key = buildFindTasksForProjectCacheKey(body);
      const cached = findTasksForProjectResolvedCache.get(key);
      if (cached) {
        return cached;
      }
      const existing = findTasksForProjectInflight.get(key);
      if (existing) {
        return existing;
      }

      const promise = (async (): Promise<U3130TaskItem[]> => {
        try {
          const response = await doFindTasksForProjectForDisplay(axios, body);
          const raw = response.data;
          const results = Array.isArray(raw) ? raw : (raw as { results?: unknown })?.results;
          const list = Array.isArray(results) ? (results as U3130TaskItem[]) : [];
          findTasksForProjectResolvedCache.set(key, list);
          return list;
        } catch (error: unknown) {
          const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
          if (errorResult.genericErrors?.length) {
            errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
          }
          return [];
        } finally {
          findTasksForProjectInflight.delete(key);
        }
      })();

      findTasksForProjectInflight.set(key, promise);
      return promise;
    },
    [axios, t, toast],
  );

  return { fetchTasks };
}
